import { useAnalyticsSessions } from '../../analytics/api';
import { SessionsTable, sessionsParams } from '../../analytics/components/sessions-table';
import { formatKyivDateTime } from '../../analytics/format';
import { t } from '../../../lib/i18n';

const DAY_MS = 86_400_000;

/**
 * Sessions of the last 30 days among the loaded rows. Rows come newest first,
 * so when another page exists and even the oldest loaded row is still inside
 * the window, the true count is larger than what is loaded — say «N+».
 */
export function sessions30dLabel(
  startedAt: readonly string[],
  hasMore: boolean,
  now: number = Date.now(),
): string {
  const since = now - 30 * DAY_MS;
  const inWindow = startedAt.filter((iso) => new Date(iso).getTime() >= since).length;
  const truncated = hasMore && inWindow === startedAt.length && inWindow > 0;
  return truncated ? `${inWindow}+` : String(inWindow);
}

/**
 * «Відвідування» — one client's visit history on the client card.
 *
 * The header reads the first page of `sessions?userId=` (the same query the
 * table below runs, so one request): the newest session's `lastAt` IS the last
 * visit — no users endpoint needed. The table hides its filters and «Хто».
 */
export function ClientVisitsTab({ userId }: { userId: string }) {
  const query = useAnalyticsSessions(sessionsParams({ app: 'all', userId }));
  const rows = query.data?.pages.flatMap((page) => page.items) ?? [];
  const newest = rows[0];

  return (
    <div className="flex flex-col gap-3" data-testid="client-visits-tab">
      {query.isSuccess && (
        <p className="rounded-cf-lg border border-cf-line bg-cf-surface px-3.5 py-3 text-[13px] lg:px-[18px]">
          {newest ? (
            <>
              <span className="font-semibold">
                {t('admin.analytics.visits.lastVisit', { at: formatKyivDateTime(newest.lastAt) })}
              </span>
              <span className="text-cf-ink-subtle">
                {' · '}
                {t('admin.analytics.visits.sessions30d', {
                  count: sessions30dLabel(
                    rows.map((row) => row.startedAt),
                    query.hasNextPage,
                  ),
                })}
              </span>
            </>
          ) : (
            <span className="text-cf-ink-subtle">{t('admin.analytics.visits.noVisits')}</span>
          )}
        </p>
      )}
      <SessionsTable userId={userId} />
    </div>
  );
}
